import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { format } from 'date-fns';

const ReminderModal = ({ isOpen, onClose, interview, onSend }) => {
  const [formData, setFormData] = useState({
    recipients: 'both',
    method: 'email',
    template: 'standard',
    customMessage: '',
    includeDetails: true
  });

  const [isSending, setIsSending] = useState(false);

  const recipientOptions = [
    { value: 'candidate', label: 'Candidate Only' },
    { value: 'interviewer', label: 'Interviewer Only' },
    { value: 'both', label: 'Both Candidate & Interviewer' }
  ];

  const methodOptions = [
    { value: 'email', label: 'Email' },
    { value: 'sms', label: 'SMS' },
    { value: 'both', label: 'Email & SMS' }
  ];

  const templateOptions = [
    { value: 'standard', label: 'Standard Reminder' },
    { value: '24hr', label: '24 Hour Reminder' },
    { value: '1hr', label: '1 Hour Reminder' },
    { value: 'custom', label: 'Custom Message' }
  ];

  const getTemplateMessage = () => {
    if (formData?.template === 'custom') {
      return formData?.customMessage;
    }

    const templates = {
      'standard': `Hi ${interview?.candidateName || '[Candidate]'},\n\nThis is a reminder about your upcoming interview for the ${interview?.position || '[Position]'} position.\n\nInterview Details:\nDate: ${interview?.scheduledDate ? format(new Date(interview?.scheduledDate), 'MMMM dd, yyyy') : '[Date]'}\nTime: ${interview?.scheduledDate ? format(new Date(interview?.scheduledDate), 'hh:mm a') : '[Time]'}\nPlatform: ${interview?.meetingPlatform || '[Platform]'}\n\nPlease join the meeting on time. Looking forward to speaking with you!\n\nBest regards,\nHiring Team`,
      '24hr': `Hi ${interview?.candidateName || '[Candidate]'},\n\nYour interview is scheduled in 24 hours!\n\nInterview for: ${interview?.position || '[Position]'}\nDate & Time: ${interview?.scheduledDate ? format(new Date(interview?.scheduledDate), 'MMMM dd, yyyy hh:mm a') : '[Date & Time]'}\n\nMeeting Link: ${interview?.meetingLink || '[Link will be provided]'}\n\nPlease ensure you have a stable internet connection and join 5 minutes early.\n\nGood luck!`,
      '1hr': `Hi ${interview?.candidateName || '[Candidate]'},\n\nYour interview starts in 1 hour!\n\nPosition: ${interview?.position || '[Position]'}\nTime: ${interview?.scheduledDate ? format(new Date(interview?.scheduledDate), 'hh:mm a') : '[Time]'}\n\nMeeting Link: ${interview?.meetingLink || '[Link]'}\n\nPlease join now to test your audio and video setup.\n\nSee you soon!`
    };

    return templates?.[formData?.template] || templates?.['standard'];
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    setIsSending(true);
    
    setTimeout(() => {
      onSend?.({
        ...formData,
        message: getTemplateMessage(),
        interview
      });
      setIsSending(false);
    }, 1500);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card border border-border rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Send Interview Reminder</h2>
            {interview && (
              <p className="text-sm text-muted-foreground mt-1">
                {interview?.candidateName} - {interview?.position}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Interview Info */}
          {interview?.scheduledDate && (
            <div className="p-4 bg-muted/50 rounded-lg space-y-2">
              <h4 className="text-sm font-medium text-foreground">Interview Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon name="Calendar" size={14} />
                  <span>{format(new Date(interview?.scheduledDate), 'MMM dd, yyyy')}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon name="Clock" size={14} />
                  <span>{format(new Date(interview?.scheduledDate), 'hh:mm a')}</span>
                </div>
                {interview?.meetingPlatform && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Icon name="Video" size={14} />
                    <span>{interview?.meetingPlatform}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Recipients */}
          <Select
            label="Send To"
            options={recipientOptions}
            value={formData?.recipients}
            onChange={(value) => setFormData({ ...formData, recipients: value })}
          />

          {/* Method */}
          <Select
            label="Delivery Method"
            options={methodOptions}
            value={formData?.method}
            onChange={(value) => setFormData({ ...formData, method: value })}
          />

          {/* Template */}
          <Select
            label="Message Template"
            options={templateOptions}
            value={formData?.template}
            onChange={(value) => setFormData({ ...formData, template: value })}
          />

          {/* Message Preview/Custom */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {formData?.template === 'custom' ? 'Custom Message' : 'Message Preview'}
            </label>
            <textarea
              rows={8}
              value={formData?.template === 'custom' ? formData?.customMessage : getTemplateMessage()}
              onChange={(e) => {
                if (formData?.template === 'custom') {
                  setFormData({ ...formData, customMessage: e?.target?.value });
                }
              }}
              readOnly={formData?.template !== 'custom'}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
            {formData?.template !== 'custom' && (
              <p className="text-xs text-muted-foreground">
                This is a preview of the message that will be sent. Select "Custom Message" to edit.
              </p>
            )}
          </div>

          {/* Include Details */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="includeDetails"
              checked={formData?.includeDetails}
              onChange={(e) => setFormData({ ...formData, includeDetails: e?.target?.checked })}
              className="h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-ring"
            />
            <label htmlFor="includeDetails" className="text-sm text-foreground">
              Include meeting link and calendar attachment
            </label>
          </div>

          {/* Info Box */}
          <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={16} className="text-primary mt-0.5" />
              <div className="text-sm text-foreground">
                <p className="font-medium">Automated Reminder Schedule</p>
                <p className="text-muted-foreground mt-1">
                  You can set up automated reminders to be sent 24 hours and 1 hour before the interview in the scheduling settings.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isSending}
              iconName="Send"
            >
              Send Reminder
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReminderModal;